import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Phone, Clock } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { services, counters, IMG } from "../lib/data";
import { getPartnerStats } from "../lib/api";
import { Icon } from "../components/Icon";
import Counter from "../components/Counter";
import SectionHeading from "../components/SectionHeading";
import TowerRecommender from "../components/TowerRecommender";
import CTASection from "../components/CTASection";
import HowItWorks from "../components/HowItWorks";
import PartnerCTA from "../components/PartnerCTA";
import WhyChoose from "../components/WhyChoose";
import OurSolutions from "../components/OurSolutions";
import ClientsPartners from "../components/ClientsPartners";

const MATERIALS_HERO_IMAGE = "https://images.unsplash.com/photo-1507333199169-84fd735371fb?auto=format&fit=crop&fm=jpg&ixlib=rb-4.1.0&q=80&w=1600";
const EUROPE_HERO_IMAGE = "https://images.unsplash.com/photo-1700716465891-9e5e9f501d7d?auto=format&fit=crop&fm=jpg&ixlib=rb-4.1.0&q=80&w=1600";

const Home = () => {
  const { t, lang, isRTL } = useLanguage();
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const [heroSlide, setHeroSlide] = useState(0);

  const heroSlides = [
    {
      image: IMG.heroTower,
      imagePosition: "center",
      alt: lang === "ar" ? "برج اتصالات حديث" : "Modern telecom tower",
      title: lang === "ar" ? "لديك مشروع اتصالات؟" : "Have a Telecom Project?",
      subtitle: lang === "ar" ? "أرسل متطلبات مشروعك واحصل على عروض من شركاء متخصصين." : "Send your project requirements and receive offers from specialized partners.",
      cta: lang === "ar" ? "اطلب عرض سعر" : "Request a Quote",
      route: "/request-quotation",
    },
    {
      image: IMG.siteWork,
      imagePosition: "center",
      alt: lang === "ar" ? "أعمال بنية تحتية في موقع مشروع" : "Infrastructure works at a project site",
      title: lang === "ar" ? "لديك مشروع بنية تحتية؟" : "Have an Infrastructure Project?",
      subtitle: lang === "ar" ? "اربط مشروعك بالمقاولين والموردين المناسبين من خلال منصة واحدة." : "Connect your project with the right contractors and suppliers through one platform.",
      cta: lang === "ar" ? "أرسل مشروعك" : "Submit Your Project",
      route: "/request-quotation",
    },
    {
      image: IMG.cityNight,
      imagePosition: "center",
      alt: lang === "ar" ? "مشروع مبانٍ حديث" : "Modern commercial buildings",
      title: lang === "ar" ? "لديك مبنى، مول أو مشروع تجاري؟" : "Have a Building, Mall or Commercial Project?",
      subtitle: lang === "ar" ? "اعرض احتياجات المشروع واحصل على حلول مناسبة من جهات متخصصة." : "Share your project needs and receive suitable solutions from specialized providers.",
      cta: lang === "ar" ? "ابدأ الآن" : "Start Now",
      route: "/request-quotation",
    },
    {
      image: MATERIALS_HERO_IMAGE,
      imagePosition: "center",
      alt: lang === "ar" ? "معدات وشبكات اتصالات" : "Telecom and network equipment",
      title: lang === "ar" ? "تحتاج معدات أو مواد لمشروعك؟" : "Need Equipment or Materials?",
      subtitle: lang === "ar" ? "اطلب معدات ومواد المشاريع بسهولة من خلال شبكة TowerLink." : "Source project equipment and materials easily through the TowerLink network.",
      cta: lang === "ar" ? "اطلب المواد" : "Request Materials",
      route: "/request-quotation",
    },
    {
      image: EUROPE_HERO_IMAGE,
      imagePosition: "center",
      alt: lang === "ar" ? "شحن واستيراد من أوروبا" : "Freight and sourcing from Europe",
      title: lang === "ar" ? "تبحث عن منتجات من أوروبا؟" : "Looking for Products from Europe?",
      subtitle: lang === "ar" ? "أرسل طلبك ودعنا نساعدك في الوصول إلى موردين ومنتجات أوروبية." : "Send your request and let us help you reach European suppliers and products.",
      cta: lang === "ar" ? "استيراد من أوروبا" : "Import from Europe",
      route: "/import-from-europe",
    },
  ];

  useEffect(() => {
    heroSlides.forEach((slide) => {
      const image = new Image();
      image.src = slide.image;
    });
  }, [lang]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setHeroSlide((current) => (current + 1) % heroSlides.length);
    }, 3000);
    return () => window.clearInterval(timer);
  }, [heroSlides.length]);

  const activeHeroSlide = heroSlides[heroSlide];

  const [partnerCount, setPartnerCount] = useState(50);
  useEffect(() => {
    getPartnerStats()
      .then((s) => setPartnerCount(50 + (s.approved_count || 0)))
      .catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative h-screen min-h-[680px] overflow-hidden grain bg-[#07101F]">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_75%_45%,rgba(212,175,55,0.08),transparent_36%),linear-gradient(135deg,#07101F_0%,#0A1628_58%,#08111E_100%)]" />
        <div className="absolute inset-y-0 end-0 w-px bg-gradient-to-b from-transparent via-[#D4AF37]/25 to-transparent" />

        <motion.div
          style={{ opacity }}
          className="relative h-full max-w-7xl mx-auto px-6 lg:px-8 flex flex-col justify-center"
        >
          <motion.div
            initial={{ opacity: 0, x: isRTL ? 30 : -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
            className="flex items-center gap-3 mb-7"
          >
            <span className="w-10 gold-line" />
            <span className="section-label">{lang === "ar" ? "منصة واحدة. حلول متعددة." : "One Platform. Multiple Solutions."}</span>
          </motion.div>

          <motion.div style={{ y }} className="grid lg:grid-cols-12 gap-9 lg:gap-14 items-center w-full">
            <div className="lg:col-span-8 min-h-[330px] flex flex-col justify-center order-1">
              <AnimatePresence mode="wait">
                <motion.div
                  key={`${lang}-${heroSlide}`}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.42, ease: "easeOut" }}
                >
                  <h1 className="font-heading text-4xl sm:text-6xl lg:text-7xl font-extrabold tracking-tighter leading-[0.95] text-white max-w-4xl">
                    {activeHeroSlide.title}
                  </h1>
                  <p className="mt-7 text-base md:text-lg text-slate-300 leading-relaxed max-w-2xl">
                    {activeHeroSlide.subtitle}
                  </p>
                  <div className="mt-10 flex flex-wrap gap-4">
                    <button
                      onClick={() => navigate(activeHeroSlide.route)}
                      data-testid="hero-rfq-btn"
                      className="btn-gold inline-flex items-center gap-2 px-8 py-4 group text-base"
                    >
                      {activeHeroSlide.cta}
                      <ArrowRight size={18} className={`transition-transform group-hover:translate-x-1 ${isRTL ? "rotate-180 group-hover:-translate-x-1" : ""}`} />
                    </button>
                    <button
                      onClick={() => navigate("/contact")}
                      data-testid="hero-engineer-btn"
                      className="inline-flex items-center gap-2 px-8 py-4 border border-white/25 text-white hover:bg-white/5 transition-colors text-base"
                    >
                      <Phone size={18} /> {t.hero.secondaryCta}
                    </button>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            <div className="hidden md:block lg:col-span-4 order-2">
              <div className="relative h-[240px] lg:h-[430px] overflow-hidden border border-white/10 bg-[#0A1223] shadow-2xl shadow-black/30">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={`image-${heroSlide}`}
                    src={activeHeroSlide.image}
                    alt={activeHeroSlide.alt}
                    initial={{ opacity: 0, scale: 1.035 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.99 }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{ objectPosition: activeHeroSlide.imagePosition }}
                  />
                </AnimatePresence>
                <div className="absolute inset-0 bg-gradient-to-t from-[#07101F]/65 via-transparent to-black/10 pointer-events-none" />
                <div className="absolute inset-y-0 start-0 w-1 bg-[#D4AF37]/90" />
              </div>
            </div>
          </motion.div>

          <div className="mt-7 flex items-center gap-2" aria-label={lang === "ar" ? "التنقل بين الرسائل" : "Hero message navigation"}>
            {heroSlides.map((slide, index) => (
              <button
                key={slide.route + index}
                type="button"
                onClick={() => setHeroSlide(index)}
                aria-label={`${lang === "ar" ? "الرسالة" : "Message"} ${index + 1}`}
                aria-current={heroSlide === index ? "true" : undefined}
                className={`h-1.5 rounded-full transition-all duration-300 ${heroSlide === index ? "w-8 bg-[#D4AF37]" : "w-3 bg-white/35 hover:bg-white/60"}`}
              />
            ))}
          </div>
        </motion.div>
      </section>

      {/* PARTNER NETWORK PROMO */}
      <PartnerCTA />

      {/* HOW IT WORKS */}
      <HowItWorks />

      {/* TRUST COUNTERS */}
      <section className="relative bg-[#11203A] border-y border-white/5 py-16" data-testid="trust-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-8 lg:gap-4">
            {counters.map((c, i) => (
              <motion.div
                key={c.key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="text-center lg:text-start lg:border-s lg:border-white/12 lg:ps-6"
              >
                <div className="font-heading text-4xl lg:text-5xl font-extrabold text-[#D4AF37]">
                  <Counter end={c.key === "partners" ? partnerCount : c.value} suffix={c.suffix} />
                </div>
                <p className="mt-2 text-sm text-slate-300">{t.trust[c.key]}</p>
              </motion.div>
            ))}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="col-span-2 lg:col-span-1 text-center lg:text-start lg:border-s lg:border-white/12 lg:ps-6"
            >
              <div className="font-heading text-4xl lg:text-5xl font-extrabold text-[#D4AF37] flex items-center justify-center lg:justify-start gap-2">
                <Clock size={28} /> {t.trust.responseValue}
              </div>
              <p className="mt-2 text-sm text-slate-300">{t.trust.response}</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-24 md:py-32" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <SectionHeading label={t.services.label} title={t.services.heading} sub={t.services.sub} />
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-px bg-white/5 border border-white/5">
            {services.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.4, delay: (i % 4) * 0.06 }}
                className="group bg-[#11203A] p-7 hover:bg-[#1A2C47] transition-colors duration-300 cursor-default"
                data-testid={`service-card-${i}`}
              >
                <div className="w-11 h-11 grid place-items-center border border-[#D4AF37]/30 text-[#D4AF37] mb-5 group-hover:bg-[#D4AF37] group-hover:text-[#0A1223] transition-colors duration-300">
                  <Icon name={s.icon} size={20} />
                </div>
                <h3 className="font-heading text-lg font-semibold text-white mb-2">{s[lang].title}</h3>
                <p className="text-sm text-slate-300 leading-relaxed">{s[lang].desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SMART RECOMMENDER */}
      <section className="py-12 md:py-20" data-testid="recommender-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <SectionHeading label={t.recommender.label} title={t.recommender.heading} sub={t.recommender.sub} />
            <img src={IMG.towerSunset} alt="" className="mt-8 w-full h-64 object-cover hidden lg:block grayscale-[0.2]" />
          </div>
          <div className="lg:col-span-7">
            <TowerRecommender />
          </div>
        </div>
      </section>

      {/* OUR SOLUTIONS */}
      <OurSolutions />

      {/* WHY CHOOSE */}
      <WhyChoose />

      {/* CLIENTS & PARTNERS */}
      <ClientsPartners />

      <CTASection />
    </div>
  );
};

export default Home;
